import React from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import styles from './ProfessionalFormActions.module.scss';
import { UnsavedChangesModal } from '../../UnsavedChangesModal/UnsavedChangesModal';
import { localStorageHelper } from '../../../../utils/localStorageHelper';
import { PROFESSIONAL_PROFILE_AUTOSAVE_KEY } from '../../../../constants';
import { useUnsavedChanges } from '../../../../context/UnsavedChangesContext';

interface ProfessionalFormActionsProps {
    isEditMode: boolean;
    submitting: boolean;
    onExit?: () => void;
    onSaveAndExit?: () => void;
    onSave?: () => void;
    isDirty?: boolean;
    lastSaved?: boolean;
    loading?: boolean;
    isReminder?: boolean;
    isSaveDisabled?: boolean;
    className?: string;
}

export const ProfessionalFormActions: React.FC<ProfessionalFormActionsProps> = ({
    isEditMode,
    submitting,
    loading,
    onExit,
    onSaveAndExit,
    onSave,
    isDirty,
    lastSaved,
    isReminder,
    isSaveDisabled,
    className
}) => {
    const { t } = useTranslation();
    const [showUnsavedModal, setShowUnsavedModal] = React.useState(false);
    const { clearReminder } = useUnsavedChanges();

    React.useEffect(() => {
        if (isReminder && isDirty) {
            setShowUnsavedModal(true);
        }
    }, [isReminder, isDirty]);

    if (!isEditMode) return null;

    const handleExitClick = () => {
        if (isDirty) {
            setShowUnsavedModal(true);
        } else {
            if (onExit) onExit();
        }
    };

    const handleCloseModal = () => {
        setShowUnsavedModal(false);
        clearReminder();
    };

    const handleConfirmExitWithoutSaving = () => {
        setShowUnsavedModal(false);
        clearReminder();
        localStorageHelper.removeItem(PROFESSIONAL_PROFILE_AUTOSAVE_KEY);
        if (onExit) {
            onExit();
        }
    };

    const handleConfirmSaveAndExit = () => {
        setShowUnsavedModal(false);
        clearReminder();
        if (onSaveAndExit) {
            onSaveAndExit();
        }
    };

    const handleSaveClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (onSave) onSave();
    };

    const isSaved = lastSaved && !isDirty;

    return (
        <div className={`${styles['save-bar']} ${className || ''}`}>
            <div className={`${styles['save-bar__pill']} ${isReminder && isDirty ? styles['save-bar__pill--green'] : ''}`}>
                
                <div className={styles['save-bar__state']}>
                    <div className={`${styles['save-bar__dot']} ${isSaved ? styles['save-bar__dot--saved'] : ''} ${isReminder && isDirty ? styles['save-bar__dot--green'] : ''}`} style={{ opacity: (isDirty || isSaved) ? 1 : 0 }} />
                    <div className={styles['save-bar__info']}>
                        <div className={styles['save-bar__title']}>
                            {isSaved 
                                ? t('appAdmin.EditProfessionalInfo.changesSaved', 'Cambios guardados')
                                : isReminder
                                    ? t('appAdmin.EditProfessionalInfo.reminderTitle', 'No olvides guardar los cambios.')
                                    : isDirty
                                        ? t('appAdmin.EditProfessionalInfo.savePrompt', 'Tenés cambios sin guardar')
                                        : t('appAdmin.EditProfessionalInfo.editing', 'Editando')
                            }
                        </div>
                        <div className={styles['save-bar__subtitle']}>
                            {isSaved
                                ? t('appAdmin.EditProfessionalInfo.profileUpdated', 'Tu perfil está actualizado.')
                                : isReminder
                                    ? t('appAdmin.EditProfessionalInfo.reminderSubtitle', 'Guardá antes de salir para no perder las modificaciones realizadas.')
                                    : isDirty
                                        ? t('appAdmin.EditProfessionalInfo.saveSubtitle', 'Guardalos para actualizar la información visible en tu perfil.')
                                        : t('appAdmin.EditProfessionalInfo.editingSubtitle', 'Modificá los campos que necesites y luego guardá los cambios.')
                            }
                        </div>
                    </div>
                </div>

                <div className={styles['save-bar__actions']}>
                    <button 
                        type="button" 
                        className={styles['save-bar__btn-exit']}
                        onClick={handleExitClick}
                    >
                        {t('appAdmin.EditProfessionalInfo.exit', 'Salir')}
                    </button>
                    
                    <button 
                        type="button" 
                        className={`${styles['save-bar__btn-save']} ${isSaved ? styles['save-bar__btn-save--saved'] : ''}`}
                        disabled={submitting || loading || isSaved || isSaveDisabled}
                        onClick={handleSaveClick}
                    >
                        {submitting || loading ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : isSaved ? (
                            t('appAdmin.EditProfessionalInfo.saved', 'Guardado')
                        ) : (
                            t('appAdmin.EditProfessionalInfo.save', 'Guardar')
                        )}
                    </button>
                </div>

            </div>

            <UnsavedChangesModal
                open={showUnsavedModal}
                onClose={() => {
                    setShowUnsavedModal(false);
                    clearReminder();
                }}
                onExitWithoutSaving={handleConfirmExitWithoutSaving}
                onSaveAndExit={handleConfirmSaveAndExit}
            />
        </div>
    );
};